import { useMemo, useState } from 'react';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';

import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

import documentAccessors from './accessorFunctions';
import { DOCUMENTS_API_ROOT } from '../../api';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';
import useDocumentSettings from '../../useDocumentSettings';
import { uploadSchema } from '../../forms/schemas';

function DocumentManagementList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);
  const store = useStore();
  const { settings, loading, error, refetch, dataProvider } =
    useDocumentSettings();
  const settingsSearchSchema = useMemo(
    () => settings && uploadSchema(searchSchema, settings),
    [settings],
  );

  const listApiUrl = `${dataProvider.getApiUrl()}${DOCUMENTS_API_ROOT}/admin`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'DOCUMENT_MANAGEMENT',
        defaultMessage: 'Document Management',
      }),
      icon: IconLibrary.Help,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'NEW_DOCUMENT',
        defaultMessage: 'New Document',
      }),
      permissionName: 'CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES',
      Icon: IconLibrary.Add,
      variant: 'outlined',
      onClick: () => {
        navigate('/document-management/create');
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, settingsSearchSchema);
    store.domain.list.set('DocumentManagementList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'DocumentManagementList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    sortBy: 'title',
    sortOrder: 'ASC',
    accessorFunctions: documentAccessors(formatMessage),
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: 'DocumentManagementList',
    IconLibrary,
    schema: settingsSearchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset('DocumentManagementList');
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name, value }) => {
    const field = settingsSearchSchema.find(item => item.name === name);
    const label = field?.options?.find(option => option.value === value)?.label;
    if (label?.localeId) {
      return formatMessage({
        id: label.localeId,
        defaultMessage: label.defaultMessage,
      });
    }
    return label || value?.label || value;
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: 'DocumentManagementList',
    schema: settingsSearchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      {loading && <LinearProgress />}
      {error && (
        <Alert
          severity="error"
          action={
            <Button onClick={refetch}>
              {formatMessage({ id: 'RETRY', defaultMessage: 'Retry' })}
            </Button>
          }>
          {formatMessage({
            id: 'DOCUMENT_SETTINGS_COULD_NOT_BE_LOADED',
            defaultMessage: 'Document settings could not be loaded.',
          })}
        </Alert>
      )}
      {settings && (
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>
      )}

      {showSearch && settings && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SEARCH_DOCUMENTS',
            defaultMessage: 'Search Documents',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'HELP_TRAINING_DOCUMENTS',
          defaultMessage: 'Help & Training Documents',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(DocumentManagementList);
